import type { QueryClient } from '@tanstack/react-query';
import { NotificationsUpdatedEventSchema } from '@magimp/shared-realtime';
import type { NotificationListResponse, NotificationPreferences } from '@magimp/shared';
import {
  NOTIFICATIONS_LIST_QK,
  NOTIFICATION_PREFS_QK,
  getActiveNotificationTarget,
  isActiveTargetMatch,
  processNotificationSoundEvent,
} from '../features/notifications/index.js';
import { markNotificationViewed } from '../api/notifications.js';
import { CARGO_PROBLEMS_QUERY_ROOT } from '../api/cargo-problems.js';

/**
 * @aiPurpose builds the socket-level handler for the `notifications:updated`
 *   wire event (#1072). Splits out of `RealtimeBootstrap` so the cohesive
 *   active-context + sound + auto-view logic lives next to its tests
 *   and the bootstrap effect remains focused on socket lifecycle.
 * @aiInvariant validation is mandatory — malformed payloads are dropped
 *   silently. The downstream invalidation re-fetches under server-side
 *   row-level visibility, so a non-recipient simply sees no change.
 * @aiInvariant (#1072 Codex round-2 F-001) only `updated` events with an
 *   active-context match fire the by-target mark-viewed write. `viewed`
 *   events are emitted by the backend AFTER a mark-viewed POST, so
 *   firing another by-target POST here would loop:
 *   `updated -> POST -> viewed -> POST -> viewed -> ...` until the
 *   drawer unmounts.
 */
export function createNotificationsUpdatedHandler(queryClient: QueryClient) {
  return (raw: unknown): void => {
    const parsed = NotificationsUpdatedEventSchema.safeParse(raw);
    if (!parsed.success) return;
    const evt = parsed.data;
    if (evt.entityType === 'CARGO_PROBLEM') {
      void queryClient.invalidateQueries({ queryKey: CARGO_PROBLEMS_QUERY_ROOT });
    }
    const active = getActiveNotificationTarget();
    const isMatch = isActiveTargetMatch(active, {
      entityType: evt.entityType,
      entityId: evt.entityId,
      notificationType: evt.notificationType,
    });
    // INVARIANT (#1073): sound playback is decided BEFORE the
    //   active-context optimistic clear so a `kind: 'viewed'` event
    //   always resets the once-per-unread cursor, and an `updated`
    //   event on an active target is suppressed for both sound and
    //   visible-unread. `processNotificationSoundEvent` is pure +
    //   side-effecting on the cursor singleton; the actual `.play()`
    //   call is fire-and-forget so a rejected autoplay never stalls
    //   the handler.
    processNotificationSoundEvent(evt, {
      isActiveMatch: isMatch,
      prefs: queryClient.getQueryData<NotificationPreferences>(NOTIFICATION_PREFS_QK),
    });
    if (isMatch && evt.kind === 'updated') {
      const prev = queryClient.getQueryData<NotificationListResponse>(NOTIFICATIONS_LIST_QK);
      if (prev) {
        const nowIso = new Date().toISOString();
        const next = prev.map((row) =>
          row.entityType === evt.entityType &&
          row.entityId === evt.entityId &&
          row.notificationType === evt.notificationType
            ? { ...row, unreadCount: 0, firstUnreadAt: null, viewedAt: nowIso }
            : row,
        );
        queryClient.setQueryData<NotificationListResponse>(NOTIFICATIONS_LIST_QK, next);
      }
      // INVARIANT (#1072 Codex round-3 F-001): for a NEW aggregate
      //   the row is not yet in the cache, so the optimistic clear
      //   above does nothing. We must wait for the by-target POST to
      //   settle BEFORE invalidating, otherwise the refetch can win
      //   the race and surface the new row as unread. Chaining via
      //   .finally invalidates on both success and error; on error
      //   the cached row would still appear unread until the next
      //   auto-view retry, which is the honest fallback.
      markNotificationViewed({
        by: 'target',
        entityType: evt.entityType,
        entityId: evt.entityId,
        notificationType: evt.notificationType,
      }).finally(() => {
        void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_LIST_QK });
      });
      return;
    }
    void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_LIST_QK });
  };
}
